import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { Message, TextOrFileMessage } from '../../types';
import { useConversation } from '../../context';
import { groupMessagesByDate } from '../../utils';
import { ChatDateSeparator } from './ChatDateSeparator';
import { ChatMessageBubble } from './ChatMessageBubble';

/**
 * The list of conversation messages.
 * - The chat message text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatDetails}
 * @see {@link ChatBox}
 */
export function ChatMessages({
    className,
    messages,
    scrollDownCounter = 0,
    isOpen,
    hideActions,
    onReplyMessage,
    onForwardMessage,
    onOpenConversation,
}: {
    className?: string;
    /**
     * The messages to render.
     */
    messages: Message[];
    /**
     * Update this variable to trigger scroll down to the bottom, except when the
     * user has manually scrolled up.
     */
    scrollDownCounter?: number;
    /**
     * The chat box is open.
     */
    isOpen: boolean;
    /**
     * Hide the actions for all message bubbles. Defaults to `false`.
     */
    hideActions?: boolean;
    /**
     * Callback when the reply button is clicked on a message.
     */
    onReplyMessage?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the forward button is clicked on a message.
     */
    onForwardMessage?: (message: TextOrFileMessage) => void;
    /**
     * Callback to open a conversation.
     */
    onOpenConversation?: (
        conversationID: number,
        professionalID: number | null,
    ) => void;
}) {
    const {
        hasMoreMessages,
        isLoading,
        loadMore,
        toggleMessageReaction,
        deleteMessage,
    } = useConversation();
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const isUserScrolled = useRef(false);
    const loadMoreBottomDistance = useRef<number | null>(null);
    const groupedMessages = useMemo(() => {
        return groupMessagesByDate(messages);
    }, [messages]);

    // Scroll to the bottom when the chat box is opened, the messages are
    // updated, or other events such as toggling reply mode (except when the
    // user has manually scrolled up).
    useLayoutEffect(() => {
        if (!isOpen) return;
        // Scroll to the bottom when previously scrolled to the bottom or when
        // the user sends a new message.
        if (!isUserScrolled.current || messages[0]?.isOptimistic) {
            containerRef.current?.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: 'instant',
            });
        }
    }, [isOpen, messages, scrollDownCounter]);

    // Trigger load more messages when the user scrolls to the top.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = containerRef.current;
        if (!sentinel || !scrollContainer || !hasMoreMessages || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting && hasMoreMessages && !isLoading) {
                    const distanceToBottom =
                        scrollContainer.scrollHeight -
                        scrollContainer.scrollTop -
                        scrollContainer.clientHeight;
                    loadMore();
                    // Scroll down when the next page finishes loading.
                    loadMoreBottomDistance.current = distanceToBottom;
                }
            },
            { root: scrollContainer },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMoreMessages, isLoading, loadMore]);

    // Scroll down to keep bottom distance after next page of messages is loaded.
    useEffect(() => {
        if (!containerRef.current || !isOpen) return;
        const observer = new MutationObserver(() => {
            if (loadMoreBottomDistance.current !== null && !isLoading) {
                if (isUserScrolled.current) {
                    containerRef.current?.scrollTo({
                        top:
                            containerRef.current.scrollHeight -
                            containerRef.current.clientHeight -
                            loadMoreBottomDistance.current,
                        behavior: 'instant',
                    });
                }
                loadMoreBottomDistance.current = null;
            }
        });
        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [isOpen, isLoading]);

    // Keep scroll pinned to bottom when content resizes (e.g. after images
    // load) or elements are added, unless the user has manually scrolled up.
    useEffect(() => {
        const content = contentRef.current;
        const container = containerRef.current;
        if (!content || !container || !isOpen) return;

        const resizeObserver = new ResizeObserver(() => {
            if (!isUserScrolled.current) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'instant',
                });
            }
        });
        const mutationObserver = new MutationObserver(() => {
            if (!isUserScrolled.current) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'instant',
                });
            }
        });

        resizeObserver.observe(content);
        mutationObserver.observe(content, {
            childList: true,
            subtree: true,
        });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [isOpen]);

    return (
        <ScrollArea
            ref={containerRef}
            className={cn(
                'rounded-[8px] bg-[#f2f2f2]',
                // Fix for width overflow issue.
                // https://github.com/radix-ui/primitives/issues/2722
                '[&>div>div]:block!',
                className,
            )}
            scrollBarClassName="w-[8px]"
            thumbClassName="bg-gray-500"
            overscroll="contain"
            onCustomScroll={(val) => {
                isUserScrolled.current = val.bottom >= 20;
                if (loadMoreBottomDistance.current !== null) {
                    loadMoreBottomDistance.current = val.bottom;
                }
            }}
        >
            <div
                ref={contentRef}
                className="relative flex flex-col gap-[8px] p-[12px] select-text"
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
            >
                {hasMoreMessages && (
                    <div
                        ref={loadMoreSentinelRef}
                        className="invisible absolute inset-x-0 top-[400px] h-px"
                        aria-hidden="true"
                    ></div>
                )}
                {groupedMessages.map(({ date, a11yString, messages }) => (
                    <React.Fragment key={a11yString}>
                        <ChatDateSeparator
                            className="mx-auto my-[8px] min-w-[25%]"
                            date={date}
                        />
                        {messages.map((message, index, messages) => {
                            const previousMessage =
                                index > 0 ? messages[index - 1] : undefined;
                            const previousSender = previousMessage?.Participant;
                            return (
                                <ChatMessageBubble
                                    key={message.ID}
                                    message={message}
                                    hideSender={
                                        previousMessage &&
                                        previousSender &&
                                        message.Participant.ID ===
                                            previousSender.ID &&
                                        previousMessage.__typename !==
                                            'SystemMessage' &&
                                        previousMessage.__typename !==
                                            'RevealContactDetailsMessage'
                                    }
                                    hideActions={hideActions}
                                    onReact={(message, reaction) => {
                                        toggleMessageReaction(
                                            Number(message.ID),
                                            reaction,
                                        );
                                    }}
                                    onReply={onReplyMessage}
                                    onForward={onForwardMessage}
                                    onDelete={() => {
                                        deleteMessage(Number(message.ID));
                                    }}
                                    onOpenConversation={onOpenConversation}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </ScrollArea>
    );
}
